import {
  BigNumber,
  Moment,
  moment,
  OrderData,
  OrderDataImpl,
  OrderState,
  OrderType,
  TradingGroupImpl,
} from "shared/cubiq-model-multi-asset";
import { CurrencyPairCalendarLookupService } from "shared/cubiq-services/lookup/model/impl/CurrencyPairCalendarLookupService";
import { TraderGroupLookupService } from "shared/cubiq-services/lookup/model/impl/TraderGroupLookupService";

const TERMINAL_ORDER_STATES: OrderState[] = [
  OrderState.FILLED,
  OrderState.CANCELLED,
  OrderState.EXPIRED,
];
const EXTENDED_INFO_KEY_EXPIRY: string = "gtcDayTDRExpiry";

export class VolumePerMinuteHelper {
  protected static lastUpdateTime: moment.Moment = null;

  constructor(
    protected traderGroupService: TraderGroupLookupService,
    protected currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
  ) {}

  /**
   * Checks if the order is a candidate for volume per minute recalculation
   * @param orderData the order to be checked
   * @returns true if the order is a candidate false otherwise
   */
  public isSupportedOrder(orderData: OrderDataImpl): boolean {
    let isAlgo: boolean =
      orderData.orderType === OrderType.ALGO || orderData.orderType == OrderType.BMALGO;
    let active: boolean = !TERMINAL_ORDER_STATES.includes(orderData.orderState);
    return isAlgo && active && this.hasExpiry(orderData);
  }

  /**
   * Checks if the order has expiry. This can be either to the order having
   * an explicit expiry time or being with a trader group that treats GTC orders
   * as DAY orders
   * @param orderData the order data to be checked
   * @returns true if the order has expiry, false otherwise
   */
  protected hasExpiry(orderData: OrderDataImpl): boolean {
    return this.getExpiryMinutes(orderData) != null;
  }

  /**
   * Get the volume per minute amount for a given order. The method does not check
   * if the order meets the criteria for volume per minute calculation. It will get
   * minutes until expiry or the interval between activation and expiry if activation
   * is present and in future. The method also takes into account GTC orders that are
   * with trader groups that treat GTC as DAY orders
   * @param openAmount the openAmount to be converted to volume per minute
   * @param orderData the order itself
   * @returns volume per minute at this moment or null if expiry cannot be determined or beyond expiry
   */
  public getVolumePerMinuteAmount(openAmount: BigNumber, orderData: OrderDataImpl): BigNumber {
    let expiryMinutes: number = this.getExpiryMinutes(orderData);
    return expiryMinutes != null && expiryMinutes > 0
      ? openAmount.dividedBy("" + expiryMinutes)
      : null;
  }

  /**
   * Determine the minutes until expiry. The method takes into account GTC orders that
   * are with trader groups that treat GTC as day
   * @param orderData the order
   * @returns the minutes until expiry in a decimal format or null if expiration cannot be determined
   */
  protected getExpiryMinutes(orderData: OrderDataImpl): number {
    let activation: moment.Moment = VolumePerMinuteHelper.lastUpdateTime;
    let expiry: moment.Moment = orderData.expiryDts;

    if (activation == null) {
      activation = VolumePerMinuteHelper.lastUpdateTime = moment().startOf("second");
    }

    // Pending activation
    if (orderData.activationDts != null && orderData.activationDts.isAfter(activation)) {
      activation = orderData.activationDts;
    } else if (
      orderData.txTime != null &&
      orderData.txTime.isAfter &&
      orderData.txTime.isAfter(activation)
    ) {
      activation = orderData.txTime;
    }

    if (expiry == null) {
      expiry = this.getExpiryForGTCOrder(orderData);
    }

    if (expiry != null) {
      let millis: number = expiry.diff(activation, "milliseconds");
      return millis / 60000;
    }

    return null;
  }

  /**
   * Get expiration time for order that is GTC. The method checks if the trader
   * group of the order is a trader group that treats GTC orders as DAY and returns
   * the system trade date rollover in such case
   * @param orderData the order
   * @returns the system trade date rollover or null if not GTC or not with a trader group
   * that converts GTC to DAY
   */
  protected getExpiryForGTCOrder(orderData: OrderData): Moment {
    let expiry: Moment = null;

    if (orderData.expiryDts == null && orderData.deskId != null) {
      let traderGroup: TradingGroupImpl = this.traderGroupService.getItem(
        orderData.deskId.toString(),
      );
      if (traderGroup != null && traderGroup.gtcAsDayOrderConfGroup === true) {
        if (
          orderData.extendedInfo != null &&
          orderData.extendedInfo.has(EXTENDED_INFO_KEY_EXPIRY)
        ) {
          return moment(orderData.extendedInfo.get(EXTENDED_INFO_KEY_EXPIRY));
        }
        expiry = this.currencyPairCalendarLookupService.systemTradeDateRollover;
        orderData.extendedInfo.set(EXTENDED_INFO_KEY_EXPIRY, expiry.toString());
      }
    }

    return expiry;
  }
}
